import React, {useState} from "react";
import i18next from "i18next";
import {useSelector} from "react-redux";
import {resetPasswordApi} from "api/auth";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {getWhitelabelId} from "store/selectors/whitelabel";
import {getSchema} from "./ResendEmailPage.schema";
import {
  paperAirplane,
  ResendEmailPage as ResendEmailPageUI,
  ResendEmailPageFormData,
  TextType,
} from "@pinch-financial/pinch-ui-components";
import useDetectKeyboardOpen from "use-detect-keyboard-open";
import NavigationBar from "components/NavigationBar/NavigationBar";
import {getIsEmailVerified} from "store/selectors/auth";
import {getHasUserAccount} from "store/selectors/user";
import {useTranslation} from "react-i18next";
import usePageText from "~/hooks/usePageText";

interface Props {
  containerRef?: React.RefObject<HTMLDivElement>;
}

const ResendEmailPage = ({containerRef}: Props) => {
  const {t: fixedT} = useTranslation("", {keyPrefix: "resendEmailPage"});
  const [sentEmail, setSentEmail] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const whiteLabelId = useSelector(getWhitelabelId);
  const isKeyboardOpen = useDetectKeyboardOpen();
  const hasUser = useSelector(getHasUserAccount);
  const isEmailVerified = useSelector(getIsEmailVerified);
  const {
    getText,
    status: textStatus,
    isTipBoxEnabled,
  } = usePageText(hasUser && !isEmailVerified ? "RESEND_EMAIL_URL" : "RESET_PASSWORD_URL");

  const formHook = useForm<ResendEmailPageFormData>({
    defaultValues: {
      email: "",
      confirmEmail: "",
    },
    resolver: yupResolver(getSchema()),
  });

  // Keep this so UI can update. React-hook-form problem.
  const {
    formState: {errors, touchedFields: touched},
  } = formHook;

  async function submitEmail(values: ResendEmailPageFormData) {
    setError("");
    setSubmitting(true);

    const {error} = await resetPasswordApi(
      whiteLabelId!,
      values.email,
      location.host,
      i18next.language
    );

    setSubmitting(false);
    if (error) {
      console.error("resetPasswordApi failed", error);
      setError(fixedT("error")!);
      return;
    }
    setSentEmail(true);
  }

  return (
    <ResendEmailPageUI
      loading={textStatus === "not-started" || textStatus === "pending"}
      isMobileKeyboardOpen={isKeyboardOpen}
      header={
        getText(TextType.APP_PAGE_HEADER) ??
        fixedT("header", {context: hasUser && !isEmailVerified ? "verify" : "forgot"})!
      }
      description={
        getText(TextType.APP_PAGE_BODY) ??
        fixedT("description", {
          context: hasUser && !isEmailVerified ? "verify" : "forgot",
        })
      }
      navigationBar={<NavigationBar />}
      iconSrc={paperAirplane}
      containerRef={containerRef}
      sentEmail={sentEmail}
      submitting={submitting}
      errorMessage={error}
      nextButtonText={getText(TextType.APP_PAGE_NEXT_BUTTON) ?? fixedT("forwardButton")}
      emailLabel={fixedT("fields.email.label")}
      emailPlaceholder={fixedT("fields.email.placeholder")}
      confirmEmailLabel={fixedT("fields.confirmEmail.label")}
      confirmEmailPlaceholder={fixedT("fields.confirmEmail.placeholder")}
      invalidEmail={fixedT("messages.invalidEmail")}
      mismatchEmail={fixedT("messages.emailsMismatch")}
      registrationCompleteMessage={i18next.t("registrationComplete.prompt", {
        context: hasUser && !isEmailVerified ? "verify" : "forgot",
      })}
      onNextPage={submitEmail}
    />
  );
};

export default ResendEmailPage;
